import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, ExternalLink, Users, ToggleLeft, ToggleRight , LogIn} from 'lucide-react';
import BrandAvatar from '../../components/BrandAvatar';
import api from '../../utils/api';
import { formatNumber } from '../../utils/constants';
import toast from 'react-hot-toast';

export default function AdminClients() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [alerts, setAlerts] = useState({});
  const [form, setForm] = useState({ name: '', subdomain: '', company_name: '', admin_username: 'admin', admin_password: '', primary_color: '#2E7DE2', background_color: '#0a0d14', secondary_color: '#161b28', default_country_code: '54' });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const loadTenants = () => {
    api.get('/admin/tenants').then(res => setTenants(res.data)).catch(console.error).finally(() => setLoading(false));
    api.get('/admin/ads-alerts').then(res => {
      const map = {};
      res.data.forEach(a => { map[a.tenant_id] = a; });
      setAlerts(map);
    }).catch(console.error);
  };
  // Abre el CRM del cliente con la sesión ya iniciada. El enlace dura 4 horas.
  const entrarAlCrm = async (t) => {
    try {
      const { data } = await api.get(`/admin/tenants/${t.id}/ver-crm`);
      window.open(data.url, '_blank', 'noopener');
    } catch { toast.error('No se pudo abrir el CRM'); }
  };

  useEffect(loadTenants, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.subdomain || !form.admin_password) { toast.error('Completá nombre, subdominio y contraseña'); return; }
    setCreating(true);
    try {
      const res = await api.post('/admin/tenants', form);
      toast.success('Cliente creado: ' + form.subdomain + '.udream.app');
      setShowCreate(false);
      setForm({ name: '', subdomain: '', company_name: '', admin_username: 'admin', admin_password: '', primary_color: '#2E7DE2', background_color: '#0a0d14', secondary_color: '#161b28', default_country_code: '54' });
      loadTenants();
    } catch (err) { toast.error(err.response?.data?.error || 'Error al crear'); }
    finally { setCreating(false); }
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800 }}>Clientes</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>{tenants.length} clientes registrados</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={18} /> Nuevo cliente</button>
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {tenants.map(t => (
          <div key={t.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <BrandAvatar src={t.logo_url} name={t.company_name || t.name} size={48} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{t.name}</div>
                <div style={{ color: 'var(--accent)', fontSize: 13 }}>{t.subdomain}.udream.app</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, fontSize: 13, color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
              <div><Users size={14} style={{ marginRight: 4 }} />{formatNumber(t.total_leads || 0)} leads</div>
              {alerts[t.id]?.google_ads?.configured && (
                <span className="badge" style={{ background: alerts[t.id].google_ads.alert ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)', color: alerts[t.id].google_ads.alert ? '#ef4444' : '#22c55e', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  G {alerts[t.id].google_ads.alert ? '⚠ Sin impresiones' : '✓'}
                </span>
              )}
              {alerts[t.id]?.meta_ads?.configured && (
                <span className="badge" style={{ background: alerts[t.id].meta_ads.alert ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)', color: alerts[t.id].meta_ads.alert ? '#ef4444' : '#22c55e', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  M {alerts[t.id].meta_ads.alert ? '⚠ Sin impresiones' : '✓'}
                </span>
              )}
              <span className="badge" style={{ background: t.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)', color: t.is_active ? '#22c55e' : '#ef4444' }}>
                {t.is_active ? 'Activo' : 'Inactivo'}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/clients/' + t.id)}><Edit2 size={14} /> Editar</button>
              <button className="btn btn-ghost btn-sm" title="Entrar a su CRM sin pedirle la contraseña"
                onClick={() => entrarAlCrm(t)}><LogIn size={14} /> Entrar</button>
              <a href={'https://' + t.subdomain + '.udream.app'} target="_blank" rel="noreferrer"
                className="btn btn-ghost btn-sm" title="Abrir la pantalla de ingreso"><ExternalLink size={14} /></a>
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <h2>Nuevo cliente</h2>
            <form onSubmit={handleCreate}>
              <div className="form-group"><label>Nombre del cliente *</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Ej: Pizzería Don Juan" required /></div>
              <div className="form-group"><label>Subdominio * (solo letras, números y guiones)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                  <input value={form.subdomain} onChange={e => setForm({...form, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})} placeholder="pizzeria" required style={{ borderRadius: '8px 0 0 8px' }} />
                  <span style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderLeft: 'none', padding: '10px 14px', fontSize: 14, color: 'var(--text-muted)', borderRadius: '0 8px 8px 0', whiteSpace: 'nowrap' }}>.udream.app</span>
                </div>
              </div>
              <div className="form-group"><label>Nombre de empresa</label><input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} placeholder="Nombre para mostrar en el CRM" /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Usuario admin *</label><input value={form.admin_username} onChange={e => setForm({...form, admin_username: e.target.value})} required /></div>
                <div className="form-group"><label>Contraseña *</label><input type="password" value={form.admin_password} onChange={e => setForm({...form, admin_password: e.target.value})} required /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                <div className="form-group"><label>Color principal</label><div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="color" value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} style={{ width: 40, height: 36, padding: 2 }} /><input value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} style={{ fontSize: 12 }} /></div></div>
              </div>
              <div className="form-group"><label>Código de país</label><input value={form.default_country_code} onChange={e => setForm({...form, default_country_code: e.target.value})} placeholder="54" /></div>
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Integraciones (opcional, se puede configurar después)</div>
                <div className="form-group"><label>Token Meta Ads</label><input value={form.meta_ads_token || ''} onChange={e => setForm({...form, meta_ads_token: e.target.value})} placeholder="EAAxxxxxxx..." style={{ fontSize: 12 }} /></div>
                <div className="form-group"><label>ID Cuenta Publicitaria Meta</label><input value={form.meta_ads_account_id || ''} onChange={e => setForm({...form, meta_ads_account_id: e.target.value})} placeholder="Ej: 708428203237366" /></div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={creating}>{creating ? 'Creando...' : 'Crear cliente'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
